import { GrowthBook } from '@growthbook/growthbook-react';
import Mixpanel from 'mixpanel-browser';
import * as Sentry from '@sentry/react';
import growthbookData from '@utils/data/growthbook';
import { getRuntimeConfigSync } from '@utils/runtimeConfig';
// Helper to get runtime config with fallback
const getGrowthBookConfig = () => {
    try {
        const config = getRuntimeConfigSync();
        return {
            apiHost: config.GROWTHBOOK_API_HOST,
            clientKey: config.GROWTHBOOK_CLIENT_KEY,
        };
    }
    catch {
        // Runtime config not yet available, return empty config
        // GrowthBook will use local data instead
        return {};
    }
};
const gbConfig = getGrowthBookConfig();
let growthBookSource = 'local';
export const getGrowthBookSource = () => growthBookSource;
export const growthBook = new GrowthBook({
    apiHost: gbConfig.apiHost,
    clientKey: gbConfig.clientKey,
    enableDevMode: process.env.NODE_ENV !== 'production',
    trackingCallback(experiment, result) {
        Mixpanel.track('$experiment_started', {
            'Experiment name': experiment.key,
            'Variant name': result.variationId,
            $source: 'growthbook',
        });
    },
});
let count = 0;
export const getGrowthBookDataWithTimeout = async () => {
    let apiHost;
    let clientKey;
    try {
        const config = getRuntimeConfigSync();
        apiHost = config.GROWTHBOOK_API_HOST;
        clientKey = config.GROWTHBOOK_CLIENT_KEY;
    }
    catch {
        console.log('[GrowthBook] Runtime config not available → using LOCAL data');
        growthBookSource = 'local';
        return growthbookData;
    }
    if (!apiHost || !clientKey) {
        console.log('[GrowthBook] Missing API host or client key → using LOCAL data');
        growthBookSource = 'local';
        return growthbookData;
    }
    const url = `${apiHost}/api/features/${clientKey}`;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
        const resp = await fetch(url, { signal: controller.signal });
        clearTimeout(timeout);
        if (!resp.ok) {
            console.log('[GrowthBook] Fetched LOCAL data');
            growthBookSource = 'local';
            return growthbookData;
        }
        console.log('[GrowthBook] Successfully fetched data from SERVER');
        growthBookSource = 'server';
        return await resp.json();
    }
    catch (e) {
        console.log('[GrowthBook] Fetched LOCAL data');
        growthBookSource = 'local';
        return growthbookData;
    }
};
export const initializeGrowthBook = async () => {
    try {
        const gbData = await getGrowthBookDataWithTimeout();
        const { features, ...rest } = gbData;
        await growthBook.init({ payload: { ...rest, features: features } });
    }
    catch (e) {
        if (count < 3) {
            count += 1;
            return initializeGrowthBook();
        }
        const errorMessage = e instanceof Error ? e.message : String(e);
        const error = new Error(`Failed to initialize GrowthBook. ${errorMessage}`);
        Sentry.captureException(error);
        return null;
    }
    return null;
};
